import React, { FC, useState, useEffect, useContext } from 'react';
import { ProfileSection } from './ProfileSection';
import { sessionStore } from 'src/store/sessionStore';
import { useUpdateTrainerProfile } from 'src/api/mutations/useUpdateTrainerProfile';
import { uploadTrainerFile } from 'src/api/services/firebaseStorage';
import { Modal } from 'antd';
import { FullScreenDocumentModal } from 'src/components/Modals/FullScreenDocumentModal/FullScreenDocumentModal';
import { toast } from 'src/utils/appAlert';
import { ProfileEditContext } from '../ProfileEditContext';
import './CareerHighlightsSection.scss';
import { ReactComponent as ProfileRemove2Icon } from '../../../../../../../assets/images/onboarding-trainer/dashboard/profile/components/profile-remove2.svg';
import { ReactComponent as ProfileRemoveIcon } from '../../../../../../../assets/images/onboarding-trainer/dashboard/profile/components/profile-remove.svg';
import { ReactComponent as CertificateIcon } from '../../../../../../../assets/images/onboarding-trainer/dashboard/profile/components/certificate.svg';
import { ReactComponent as UploadIcon } from '../../../../../../../assets/images/onboarding-trainer/dashboard/profile/components/upload.svg';
import { ReactComponent as CloseIcon } from '../../../../../../../assets/images/onboarding-trainer/dashboard/profile/components/close.svg';

export const CareerHighlightsSection: FC = () => {
  const { currentUser } = sessionStore();
  const [isEditing, setIsEditing] = useState(false);
  const [uploadingIndex, setUploadingIndex] = useState<number | null>(null);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const { mutateAsync: updateProfileBackend, isPending } = useUpdateTrainerProfile();

  const { globalEditAction, registerEditState } = useContext(ProfileEditContext);

  useEffect(() => {
    if (globalEditAction.count > 0) {
      if (globalEditAction.type === 'OPEN') {
        setIsEditing(true);
        setHasSubmitted(false);
      } else if (globalEditAction.type === 'CLOSE') {
        setIsEditing(false);
      }
    }
  }, [globalEditAction]);

  useEffect(() => {
    registerEditState('careerHighlights', isEditing);
  }, [isEditing, registerEditState]);

  const [certificates, setCertificates] = useState<
    { name: string; fileUrl?: string; fileName?: string; error?: string }[]
  >(currentUser?.certificates?.length ? currentUser.certificates : [{ name: '' }]);

  const [previewState, setPreviewState] = useState<{
    url: string;
    type: 'image' | 'pdf' | 'doc' | 'docx';
  } | null>(null);

  const [certToRemove, setCertToRemove] = useState<number | null>(null);

  useEffect(() => {
    if (currentUser && !isEditing) {
      setCertificates(currentUser.certificates?.length ? currentUser.certificates : [{ name: '' }]);
    }
  }, [currentUser, isEditing]);

  const handleSave = async () => {
    if (!currentUser) return;

    if (
      certificates.length === 0 &&
      (!currentUser?.certificates || currentUser.certificates.length === 0)
    ) {
      return;
    }

    setHasSubmitted(true);

    const hasErrors = certificates.some((c) => c.name.trim() === '' || !c.fileUrl);
    const hasUploadErrors = certificates.some((c) => c.error);

    if (hasErrors || hasUploadErrors) {
      toast.error(
        'Please resolve certificate validation errors or remove invalid entries before saving.',
      );
      return;
    }

    const validCerts = certificates.filter((c) => c.name.trim() !== '' && c.fileUrl);

    // Check lengths
    const invalidName = validCerts.find((c) => c.name.length > 100);
    if (invalidName) {
      toast.error('Certificate name must be less than 100 characters');
      return;
    }

    try {
      const uid = currentUser?.uid || '';
      await updateProfileBackend({
        uid,
        data: { certificates: validCerts },
      });
      setIsEditing(false);
    } catch (error) {
      console.error(error);
    }
  };

  const handleAddCert = () => {
    setCertificates([...certificates, { name: '' }]);
    setHasSubmitted(false);
  };

  const handleRemoveCert = (index: number) => {
    const cert = certificates[index];
    if (!cert.name.trim() && !cert.fileUrl && !cert.fileName) {
      setCertificates(certificates.filter((_, i) => i !== index));
    } else {
      setCertToRemove(index);
    }
  };

  const confirmRemoveCert = () => {
    if (certToRemove !== null) {
      setCertificates(certificates.filter((_, i) => i !== certToRemove));
      setCertToRemove(null);
    }
  };

  const handleUpdateCertName = (index: number, name: string) => {
    setCertificates(certificates.map((c, i) => (i === index ? { ...c, name } : c)));
  };

  const handleFileUpload = async (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !currentUser) return;

    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'image/png',
      'image/jpeg',
      'image/jpg',
      'image/gif',
      'image/webp',
    ];

    if (!allowedTypes.includes(file.type)) {
      setCertificates(
        certificates.map((c, i) => {
          if (i === index) {
            const fileNameWithoutExt = file.name.replace(/\.[^/.]+$/, '');
            return {
              ...c,
              fileName: file.name,
              name: c.name.trim() === '' ? fileNameWithoutExt : c.name,
              error: 'Unsupported file format',
            };
          }
          return c;
        }),
      );
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      setCertificates(
        certificates.map((c, i) => {
          if (i === index) {
            const fileNameWithoutExt = file.name.replace(/\.[^/.]+$/, '');
            return {
              ...c,
              fileName: file.name,
              name: c.name.trim() === '' ? fileNameWithoutExt : c.name,
              error: 'File size exceeds 2MB limit',
            };
          }
          return c;
        }),
      );
      return;
    }

    // Clear any previous error before starting upload
    setCertificates(certificates.map((c, i) => (i === index ? { ...c, error: undefined } : c)));

    try {
      setUploadingIndex(index);
      const uid = currentUser?.uid || '';
      const downloadUrl = await uploadTrainerFile(file, 'certificates', uid);

      setCertificates(
        certificates.map((c, i) => {
          if (i === index) {
            const fileNameWithoutExt = file.name.replace(/\.[^/.]+$/, '');
            return {
              ...c,
              fileUrl: downloadUrl,
              fileName: file.name,
              name: c.name.trim() === '' ? fileNameWithoutExt : c.name,
              error: undefined,
            };
          }
          return c;
        }),
      );
    } catch {
      toast.error('Failed to process file');
      setCertificates(
        certificates.map((c, i) => {
          if (i === index) {
            return {
              ...c,
              error: 'Upload failed',
            };
          }
          return c;
        }),
      );
    } finally {
      setUploadingIndex(null);
    }
  };

  const isAddDisabled = uploadingIndex !== null || certificates.length >= 5;
  const hasUploadError = certificates.some((c) => c.error);

  const handleView = (cert: { name: string; fileUrl?: string }) => {
    if (!cert.fileUrl) return;

    // Detect if the stored URL is an image or pdf based on extension from firebase storage or token
    const isPdf = cert.fileUrl.toLowerCase().includes('.pdf');
    const isImage =
      cert.fileUrl.match(/\.(jpeg|jpg|gif|png)/i) != null ||
      (!isPdf && cert.fileUrl.includes('firebasestorage'));

    if (isPdf || isImage) {
      setPreviewState({ url: cert.fileUrl, type: isPdf ? 'pdf' : 'image' });
    } else {
      window.open(cert.fileUrl, '_blank');
    }
  };

  const viewContent = (
    <div className="career-highlights-view">
      {currentUser?.certificates && currentUser.certificates.length > 0 ? (
        currentUser.certificates.map((cert, index) => (
          <div key={index} className="certificate-item">
            <div className="cert-info">
              {<CertificateIcon />}
              <span>{cert.name}</span>
            </div>
            {cert.fileUrl && (
              <button className="view-btn" onClick={() => handleView(cert)}>
                {'View'}
              </button>
            )}
          </div>
        ))
      ) : (
        <>
          <div className="text-section-view">
            <div className="icon-wrapper">{<CertificateIcon />}</div>
            <p>{'No certificates added yet.'}</p>
          </div>
        </>
      )}
    </div>
  );

  const editContent = (
    <div className="career-highlights-edit">
      <div className="edit-description-row">
        <p className="description">Upload your certifications and achievements</p>
        <button className="add-action-btn" onClick={handleAddCert} disabled={isAddDisabled}>
          {'+ Add Certificate'}
        </button>
      </div>

      {certificates.map((cert, index) => (
        <div key={index} className="cert-input-group">
          <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
            <div className="form-group cert-name-group" style={{ marginBottom: 0 }}>
              <input
                type="text"
                placeholder="Certificate name"
                value={cert.name}
                maxLength={100}
                onChange={(e) => handleUpdateCertName(index, e.target.value)}
                onBlur={(e) => handleUpdateCertName(index, e.target.value.trim())}
                className={hasSubmitted && cert.name.trim() === '' ? 'error-border' : ''}
              />
              <button className="remove-cert-btn" onClick={() => handleRemoveCert(index)}>
                {<CloseIcon />}
              </button>
            </div>
            {hasSubmitted && cert.name.trim() === '' && (
              <small
                className="error-message"
                style={{ color: '#E44E4E', marginTop: '4px', display: 'block', fontSize: '12px' }}
              >
                Certificate name is required
              </small>
            )}
          </div>
          {cert.fileUrl || cert.error ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <div
                className={`uploaded-file ${cert.error ? 'error-border' : ''}`}
                onClick={() => !cert.error && handleView(cert as any)}
                style={{
                  cursor: cert.error ? 'default' : 'pointer',
                  color: cert.error ? '#E44E4E' : 'inherit',
                }}
              >
                {<CertificateIcon />} <span>{cert.fileName || 'Uploaded Document'}</span>
              </div>
              {cert.error && <small style={{ color: '#E44E4E' }}>{cert.error}</small>}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <div
                className={`upload-cert-btn ${hasSubmitted && !cert.fileUrl ? 'error-border' : ''}`}
                style={{
                  position: 'relative',
                  overflow: 'hidden',
                  opacity: uploadingIndex === index ? 0.5 : 1,
                }}
              >
                {uploadingIndex === index ? (
                  <svg
                    className="loading-spinner"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                  </svg>
                ) : (
                  <>{<UploadIcon />} Upload certificate</>
                )}
                <input
                  type="file"
                  accept=".pdf,.doc,.docx,image/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  onChange={(e) => handleFileUpload(index, e)}
                  disabled={uploadingIndex !== null}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    opacity: 0,
                    cursor: uploadingIndex !== null ? 'not-allowed' : 'pointer',
                  }}
                />
              </div>
              {hasSubmitted && !cert.fileUrl && (
                <small
                  className="error-message"
                  style={{ color: '#E44E4E', marginTop: '4px', display: 'block', fontSize: '12px' }}
                >
                  Certificate file is required
                </small>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );

  const isInitialEmpty = !currentUser?.certificates || currentUser.certificates.length === 0;
  const isActionDisabled =
    uploadingIndex !== null ||
    hasUploadError ||
    certificates.some((c) => c.name.trim() === '' || !c.fileUrl) ||
    (certificates.length === 0 && isInitialEmpty);

  return (
    <>
      <ProfileSection
        className="align-items-center career-highlights-centificate-header"
        title="Career Highlights & Certificates"
        isEditing={isEditing}
        isSaving={isPending}
        isActionDisabled={isActionDisabled}
        onEditToggle={setIsEditing}
        onSave={handleSave}
        onCancel={() => {
          if (currentUser) {
            setCertificates(
              currentUser.certificates?.length ? currentUser.certificates : [{ name: '' }],
            );
          }
        }}
        editContent={editContent}
      >
        {viewContent}
      </ProfileSection>

      <FullScreenDocumentModal
        isOpen={Boolean(previewState)}
        onClose={() => setPreviewState(null)}
        documentUrl={previewState?.url || ''}
        type={previewState?.type}
        title="Certificate Preview"
      />

      <Modal
        open={certToRemove !== null}
        footer={null}
        onCancel={() => setCertToRemove(null)}
        centered
        width={400}
        closeIcon={null}
        rootClassName="logout-modal"
        className="logout-modal"
        styles={{
          mask: {
            backdropFilter: 'blur(4px)',
            backgroundColor: 'rgba(6, 7, 35, 0.8)',
          },
        }}
      >
        <div className="logout-modal-content">
          <div className="logout-icon-wrapper">
            <ProfileRemove2Icon />
          </div>
          <h2>Delete Certification?</h2>
          <p>
            Are you sure you want to delete your{' '}
            {certToRemove !== null && certificates[certToRemove]?.name
              ? certificates[certToRemove].name
              : 'certification'}
            ?
          </p>
          <div className="logout-actions">
            <button className="btn-logout" onClick={confirmRemoveCert}>
              {<ProfileRemoveIcon />} Delete
            </button>
            <button className="btn-cancel" onClick={() => setCertToRemove(null)}>
              Cancel
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};
